import { useLocale, useLocaleString } from '../../../hooks/useLocale';
import { getUseStateSelector } from '../../../store';
import {
  PropertyServiceTypeModel,
  PropertyTypeModel,
} from '../../Property/ManageCreateProperty/models/property_type.model';

interface BidAmount {
  amount: number | null;
  volume: number;
}

export interface PropertyImageFile {
  id: number;
  created_at: Date;
  modified_at: Date;
  image: string;
  is_cover: boolean;
  property: number;
}

export enum PropertyOwnershipType {
  fractional_ownership = 'fractional_ownership',
  separate_ownership = 'separate_ownership',
}

interface IProperty {
  id: number;
  property_long_name: string;
  property_long_name_en: string;
  property_trading_name: string;
  property_trading_name_en: string;
  is_crowd_funding: boolean;
  opening_price: number;
  volume: number;
  bid: BidAmount;
  ask: BidAmount;
  yield_percent: number;
  last_price: number;
  closed_price: number;
  reference_price: string;
  initial_unit_price: number;
  is_watchlisted: boolean;
  property_images: PropertyImageFile[];
  property_city_ar: string;
  property_locality_ar: string;
  pincode: string;
  country_en: string;
  property_city: string;
  property_locality: string;
  country_ar: string;
  local_trade_only: boolean;
  min_buy_order_value: number;
  //
  ownership_type: PropertyOwnershipType;
  property_type: PropertyTypeModel;
}

class PropertyModel implements IProperty {
  id: number;
  property_long_name: string;
  property_long_name_en: string;
  property_trading_name: string;
  property_trading_name_en: string;
  is_crowd_funding: boolean;
  opening_price: number;
  volume: number;
  bid: BidAmount;
  ask: BidAmount;
  yield_percent: number;
  last_price: number;
  closed_price: number;
  reference_price: string;
  initial_unit_price: number;
  is_watchlisted: boolean;
  property_images: PropertyImageFile[];
  property_city_ar: string;
  property_locality_ar: string;
  pincode: string;
  country_en: string;
  property_city: string;
  property_locality: string;
  country_ar: string;
  local_trade_only: boolean;
  min_buy_order_value: number;
  //
  ownership_type: PropertyOwnershipType;
  property_type: PropertyTypeModel;

  constructor(data: Partial<IProperty>) {
    this.id = data.id ?? 0;
    this.property_long_name = data.property_long_name ?? '';
    this.property_long_name_en = data.property_long_name_en ?? '';
    this.property_trading_name = data.property_trading_name ?? '';
    this.property_trading_name_en = data.property_trading_name_en ?? '';
    this.is_crowd_funding = data.is_crowd_funding ?? false;
    this.opening_price = data.opening_price ?? 0;
    this.volume = data.volume ?? 0;
    this.bid = data.bid ?? { amount: null, volume: 0 };
    this.ask = data.ask ?? { amount: null, volume: 0 };
    this.yield_percent = data.yield_percent ?? 0;
    this.last_price = data.last_price ?? 0;
    this.closed_price = data.closed_price ?? 0;
    this.reference_price = data.reference_price ?? '';
    this.initial_unit_price = data.initial_unit_price ?? 0;
    this.is_watchlisted = data.is_watchlisted ?? false;
    this.property_images = data.property_images ?? [];
    this.property_city_ar = data.property_city_ar ?? '';
    this.property_locality_ar = data.property_locality_ar ?? '';
    this.pincode = data.pincode ?? '';
    this.country_en = data.country_en ?? '';
    this.property_city = data.property_city ?? '';
    this.property_locality = data.property_locality ?? '';
    this.country_ar = data.country_ar ?? '';
    this.local_trade_only = data.local_trade_only ?? false;
    this.min_buy_order_value = data.min_buy_order_value ?? 0;
    //
    this.ownership_type =
      data.ownership_type ?? PropertyOwnershipType.fractional_ownership;
    this.property_type = data.property_type ?? new PropertyTypeModel({});
  }

  /** From json to class instance */
  static fromJson(data: IProperty): PropertyModel {
    return new PropertyModel({
      ...data,
      property_images:
        data?.property_images &&
        data.property_images.map((img) => ({
          ...img,
          created_at: new Date(img.created_at),
          modified_at: new Date(img.modified_at),
        })),
      property_type: PropertyTypeModel.fromJson(data.property_type ?? {}),
    });
  }

  /** from class to json */
  toJson(): IProperty {
    return {
      id: this.id,
      property_long_name: this.property_long_name,
      property_long_name_en: this.property_long_name_en,
      property_trading_name: this.property_trading_name,
      property_trading_name_en: this.property_trading_name_en,
      is_crowd_funding: this.is_crowd_funding,
      opening_price: this.opening_price,
      volume: this.volume,
      bid: this.bid,
      ask: this.ask,
      yield_percent: this.yield_percent,
      last_price: this.last_price,
      closed_price: this.closed_price,
      reference_price: this.reference_price,
      initial_unit_price: this.initial_unit_price,
      is_watchlisted: this.is_watchlisted,
      property_images: this.property_images,
      property_city_ar: this.property_city_ar,
      property_locality_ar: this.property_locality_ar,
      pincode: this.pincode,
      country_en: this.country_en,
      property_city: this.property_city,
      property_locality: this.property_locality,
      country_ar: this.country_ar,
      local_trade_only: this.local_trade_only,
      min_buy_order_value: this.min_buy_order_value,
      //
      ownership_type: this.ownership_type,
      property_type: this.property_type,
    };
  }
  get isFractionalOwnership() {
    return this.ownership_type === PropertyOwnershipType.fractional_ownership;
  }

  get isSeperateOwnership() {
    return this.ownership_type === PropertyOwnershipType.separate_ownership;
  }
  get propertyName(): string {
    const { isArabic } = useLocale();
    return isArabic ? this.property_long_name : this.property_long_name_en;
  }

  /** 'copyWith' is the function to copy the instance of the class */
  copyWith(data: Partial<IProperty>): PropertyModel {
    return new PropertyModel({ ...this.toJson(), ...data });
  }

  get coverImage(): PropertyImageFile | null {
    if (!this.property_images || this.property_images.length === 0) return null;
    const img = this.property_images.find((img) => img.is_cover);
    return img ?? this.property_images[0];
  }

  get address(): string {
    const city = useLocaleString(this.property_city_ar, this.property_city);
    const locality = useLocaleString(
      this.property_locality_ar,
      this.property_locality
    );
    return `${locality}, ${city}`;
  }
}

export default PropertyModel;
